import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isIdleByDesign } from '../../../../shared/lib/epicStateText';
import { useSearchParams } from 'react-router-dom';
import type { EpicDetail, EpicSession } from '../../../../shared/types/epics';
import { agentEmoji } from '../../lib/agentEmoji';

/**
 * Swim-lane visualization of an epic's life.
 *
 * One horizontal lane per story (default) or per agent type. Each agent
 * session is a coloured rectangle on its lane, positioned by `startedAt`
 * and sized by `durationMs`. Running sessions pulse.
 *
 * Controls:
 *  - Legend chips filter by agent type (click to hide / show).
 *  - "Group" toggles lanes between story-per-lane and agent-per-lane.
 *  - "Window" presets re-bound the time axis (full / since /proceed / 1h / 24h).
 *  - "Pause" stops the live refresh + the now-cursor (managed by the parent).
 *  - Hovering a session block dims every block from other agents.
 *
 * Click any block to open the existing TranscriptDrawer for that
 * (story, agent).
 */
interface EpicPipelineProps {
  epic: EpicDetail;
  /** Opens the transcript drawer the existing detail page mounts. */
  onOpenTranscript: (storyId: string, agentType: string) => void;
  /** Whether the parent has paused its 15s refetch. Controls the now-cursor. */
  paused: boolean;
  /** Toggle live updates on/off (lifts pause control out to the parent). */
  onTogglePaused: () => void;
}

const AGENT_PALETTE: Record<string, { fill: string; text: string }> = {
  'epic-planner':    { fill: '#0052CC', text: '#FFFFFF' },
  'graph-architect': { fill: '#185FA5', text: '#FFFFFF' },
  'planner':         { fill: '#0052CC', text: '#FFFFFF' },
  'backend':         { fill: '#1FA8C9', text: '#FFFFFF' },
  'backend-review':  { fill: '#0F6E56', text: '#FFFFFF' },
  'web':             { fill: '#E2A100', text: '#412402' },
  'web-review':      { fill: '#BA7517', text: '#FFFFFF' },
  'mobile':          { fill: '#D85A30', text: '#FFFFFF' },
  'mobile-review':   { fill: '#993C1D', text: '#FFFFFF' },
  'qa':              { fill: '#22A06B', text: '#FFFFFF' },
  'qa-dev-validate': { fill: '#3B6D11', text: '#FFFFFF' },
  'qa-test-validate':{ fill: '#3B6D11', text: '#FFFFFF' },
  'devops':          { fill: '#8270DB', text: '#FFFFFF' },
  'secops':          { fill: '#D4537E', text: '#FFFFFF' },
  'docs':            { fill: '#5F5E5A', text: '#FFFFFF' },
};
const FALLBACK_COLOR = { fill: '#888780', text: '#FFFFFF' };
function colorFor(agent: string) { return AGENT_PALETTE[agent] ?? FALLBACK_COLOR; }

const VIEW_W = 1100;
const PAD = { top: 24, right: 32, bottom: 36, left: 160 };
const LANE_H = 36;
const LANE_GAP = 6;
const MIN_BLOCK_W = 4;
const NOW_REFRESH_MS = 15_000;

type GroupBy = 'story' | 'agent';
type Range = 'full' | 'proceed' | '1h' | '24h';

interface Lane {
  /** Stable lane id used for keys + (story-mode) onOpenTranscript routing. */
  id: string;
  label: string;
  subLabel: string | null;
  isOrphan: boolean;
  sessions: EpicSession[];
}

function laneSortAlpha(a: string, b: string): number { return a.localeCompare(b); }

function buildLanesByStory(epic: EpicDetail): Lane[] {
  const knownIds = new Set(epic.stories.map((s) => s.id));
  const byStoryId = new Map<string, EpicSession[]>();
  for (const s of epic.sessions) {
    const list = byStoryId.get(s.storyId) ?? [];
    list.push(s);
    byStoryId.set(s.storyId, list);
  }
  const storyLanes: Lane[] = epic.stories.map((s) => ({
    id: s.id,
    label: s.id,
    subLabel: s.storyPoints !== null ? `${s.storyPoints} SP` : null,
    isOrphan: false,
    sessions: byStoryId.get(s.id) ?? [],
  }));
  const orphanIds = Array.from(byStoryId.keys()).filter((id) => !knownIds.has(id)).sort(laneSortAlpha);
  const orphanLanes: Lane[] = orphanIds.map((id) => ({
    id, label: id, subLabel: null, isOrphan: true, sessions: byStoryId.get(id) ?? [],
  }));
  return [...orphanLanes, ...storyLanes];
}

function buildLanesByAgent(epic: EpicDetail): Lane[] {
  const byAgent = new Map<string, EpicSession[]>();
  for (const s of epic.sessions) {
    const list = byAgent.get(s.agentType) ?? [];
    list.push(s);
    byAgent.set(s.agentType, list);
  }
  return Array.from(byAgent.keys()).sort(laneSortAlpha).map((agent) => ({
    id: `agent:${agent}`,
    label: agent,
    subLabel: `${byAgent.get(agent)!.length} runs`,
    isOrphan: false,
    sessions: byAgent.get(agent)!,
  }));
}

function useTicker(paused: boolean): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setNow(Date.now()), NOW_REFRESH_MS);
    return () => clearInterval(id);
  }, [paused]);
  return now;
}

function formatHm(iso: string | number): string {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }); }
  catch { return String(iso); }
}
function formatMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
}
function formatCost(usd: number): string {
  if (!Number.isFinite(usd) || usd === 0) return '$0';
  if (usd < 0.01) return `$${usd.toFixed(6)}`;
  return `$${usd.toFixed(4)}`;
}

export const EpicPipeline: React.FC<EpicPipelineProps> = ({
  epic, onOpenTranscript, paused, onTogglePaused,
}) => {
  const now = useTicker(paused);

  // ── Control state ─────────────────────────────────────────────────────
  // Deep-linking: group-by / range / hidden-agent filter live in the URL
  // (?group=&range=&hide=a,b) so a shared epic link reproduces the exact
  // pipeline view. Hover + the clock tick are transient and stay local.
  const [searchParams, setSearchParams] = useSearchParams();
  const groupParam = searchParams.get('group');
  const groupBy: GroupBy = groupParam === 'agent' ? 'agent' : 'story';
  const rangeParam = searchParams.get('range');
  const range: Range = (rangeParam === 'proceed' || rangeParam === '1h' || rangeParam === '24h')
    ? (rangeParam as Range) : 'full';
  const hiddenAgents = useMemo<Set<string>>(() => {
    const raw = searchParams.get('hide');
    return new Set(raw ? raw.split(',').filter(Boolean) : []);
  }, [searchParams]);
  const [hoverAgent, setHoverAgent] = useState<string | null>(null);

  const updateParams = useCallback((mutate: (p: URLSearchParams) => void) => {
    const p = new URLSearchParams(searchParams);
    mutate(p);
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  const setGroupBy = useCallback((next: GroupBy) => {
    updateParams((p) => { if (next === 'story') p.delete('group'); else p.set('group', next); });
  }, [updateParams]);
  const setRange = useCallback((next: Range) => {
    updateParams((p) => { if (next === 'full') p.delete('range'); else p.set('range', next); });
  }, [updateParams]);
  const toggleAgent = useCallback((agent: string) => {
    updateParams((p) => {
      const next = new Set((p.get('hide') ?? '').split(',').filter(Boolean));
      if (next.has(agent)) next.delete(agent); else next.add(agent);
      if (next.size === 0) p.delete('hide'); else p.set('hide', [...next].sort().join(','));
    });
  }, [updateParams]);
  const clearAgentFilter = useCallback(() => {
    updateParams((p) => p.delete('hide'));
  }, [updateParams]);

  // ── Lane build (pre-filter, so the legend always sees every agent) ────
  const allLanes = useMemo(
    () => (groupBy === 'agent' ? buildLanesByAgent(epic) : buildLanesByStory(epic)),
    [epic, groupBy],
  );

  // Every agent that's ever appeared in this epic — drives the legend.
  const agentsInUse = useMemo(() => {
    const set = new Set<string>();
    for (const s of epic.sessions) set.add(s.agentType);
    return Array.from(set).sort();
  }, [epic.sessions]);

  // Drop any lane (in agent-mode) or any session (in story-mode) belonging
  // to a hidden agent. In agent-mode, hiding the agent hides the lane;
  // in story-mode, only the session blocks disappear so lane order stays.
  const filteredLanes = useMemo<Lane[]>(() => {
    if (groupBy === 'agent') {
      return allLanes.filter((l) => !hiddenAgents.has(l.label));
    }
    return allLanes.map((l) => ({
      ...l,
      sessions: l.sessions.filter((s) => !hiddenAgents.has(s.agentType)),
    }));
  }, [allLanes, hiddenAgents, groupBy]);

  // ── Time bounds (drive xOf) ───────────────────────────────────────────
  const approvedAt = epic.github.approvedAt
    ? new Date(epic.github.approvedAt).getTime()
    : null;

  const { tMin, tMax } = useMemo(() => {
    const starts: number[] = [];
    const ends: number[] = [];
    for (const lane of filteredLanes) {
      for (const s of lane.sessions) {
        const start = new Date(s.startedAt).getTime();
        if (Number.isFinite(start)) starts.push(start);
        const end = s.finishedAt ? new Date(s.finishedAt).getTime() : now;
        if (Number.isFinite(end)) ends.push(end);
      }
    }
    const fallbackMin = now - 60_000;
    const baseMin = starts.length === 0 ? fallbackMin : Math.min(...starts);
    const baseMax = Math.max(now, ...(ends.length === 0 ? [now] : ends));
    switch (range) {
      case '1h':       return { tMin: now - 60 * 60_000, tMax: now };
      case '24h':      return { tMin: now - 24 * 60 * 60_000, tMax: now };
      case 'proceed':  return { tMin: approvedAt ?? baseMin, tMax: baseMax };
      case 'full':
      default:         return { tMin: baseMin, tMax: baseMax };
    }
  }, [filteredLanes, now, range, approvedAt]);

  const innerW = VIEW_W - PAD.left - PAD.right;
  const totalLanes = Math.max(1, filteredLanes.length);
  const innerH = totalLanes * LANE_H + (totalLanes - 1) * LANE_GAP;
  const viewH = PAD.top + innerH + PAD.bottom;
  const span = Math.max(1, tMax - tMin);
  const xOf = (t: number) => PAD.left + ((t - tMin) / span) * innerW;
  const laneY = (i: number) => PAD.top + i * (LANE_H + LANE_GAP);
  const sessionEnd = (s: EpicSession): number =>
    s.finishedAt ? new Date(s.finishedAt).getTime() : Math.max(new Date(s.startedAt).getTime() + 1000, now);

  // ── Counts for header ─────────────────────────────────────────────────
  const total = epic.sessions.length;
  const visible = useMemo(
    () => filteredLanes.reduce((a, l) => a + l.sessions.length, 0),
    [filteredLanes],
  );
  const runningVisible = useMemo(
    () => filteredLanes.reduce((a, l) => a + l.sessions.filter((s) => s.isRunning).length, 0),
    [filteredLanes],
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden" data-testid="epic-pipeline">
      {/* Card header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
        <div className="text-sm font-semibold text-slate-900">Live pipeline</div>
        <div className="text-xs text-slate-500" data-testid="pipeline-counts">
          {total === 0 ? (
            <>{isIdleByDesign(epic.state) ? 'No agents running' : 'Waiting for first agent…'}</>
          ) : (
            <>
              {visible} of {total} runs
              {runningVisible > 0 && (
                <> · <span className="text-accent-600 font-medium">{runningVisible} running</span></>
              )}
            </>
          )}
        </div>
      </div>

      {/* Control bar */}
      <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center gap-3">
        <Segmented<GroupBy>
          label="Group"
          value={groupBy}
          onChange={setGroupBy}
          options={[
            { value: 'story', label: 'Story' },
            { value: 'agent', label: 'Agent' },
          ]}
          testid="group-by"
        />
        <Segmented<Range>
          label="Window"
          value={range}
          onChange={setRange}
          options={[
            { value: 'full', label: 'Full' },
            { value: 'proceed', label: 'Since /proceed', disabled: !approvedAt },
            { value: '1h', label: '1h' },
            { value: '24h', label: '24h' },
          ]}
          testid="range"
        />
        <div className="ml-auto">
          <button
            type="button"
            onClick={onTogglePaused}
            aria-pressed={paused}
            data-testid="toggle-paused"
            className={
              'inline-flex items-center gap-1.5 h-7 px-3 rounded-md text-xs font-medium border transition-colors ' +
              (paused
                ? 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50')
            }
          >
            <span aria-hidden>{paused ? '▶' : '⏸'}</span>
            {paused ? 'Resume live' : 'Pause live'}
          </button>
        </div>
      </div>

      {/* Legend / filter pills */}
      {agentsInUse.length > 0 && (
        <div className="px-5 py-2 border-b border-slate-200 flex flex-wrap items-center gap-1.5" data-testid="agent-legend">
          {agentsInUse.map((agent) => {
            const hidden = hiddenAgents.has(agent);
            const c = colorFor(agent);
            return (
              <button
                key={agent}
                type="button"
                aria-pressed={!hidden}
                onClick={() => toggleAgent(agent)}
                onMouseEnter={() => setHoverAgent(agent)}
                onMouseLeave={() => setHoverAgent(null)}
                data-testid={`agent-chip-${agent}`}
                className={
                  'inline-flex items-center gap-1.5 h-6 px-2 rounded-full border text-2xs transition ' +
                  (hidden
                    ? 'bg-slate-50 text-slate-400 border-slate-200 line-through'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50')
                }
              >
                <span
                  aria-hidden
                  className="inline-block w-2 h-2 rounded-sm"
                  style={{ backgroundColor: hidden ? '#CBD5E1' : c.fill }}
                />
                {agent}
              </button>
            );
          })}
          {hiddenAgents.size > 0 && (
            <button
              type="button"
              onClick={clearAgentFilter}
              data-testid="agent-clear"
              className="ml-1 text-2xs text-primary-500 hover:text-primary-600 hover:underline"
            >
              show all
            </button>
          )}
        </div>
      )}

      {/* SVG canvas */}
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${VIEW_W} ${viewH}`}
          className="w-full block"
          style={{ minWidth: 720 }}
          role="img"
          aria-label="Epic agent pipeline"
        >
          <title>Live pipeline for epic #{epic.number}</title>

          {/* Lane backgrounds + labels */}
          {filteredLanes.map((lane, i) => {
            const y = laneY(i);
            return (
              <g key={lane.id}>
                <rect
                  x={PAD.left} y={y} width={innerW} height={LANE_H} rx={6}
                  fill={i % 2 === 0 ? '#F8FAFC' : '#FFFFFF'}
                  stroke="#E2E8F0" strokeWidth={0.5}
                />
                <text
                  x={PAD.left - 10} y={y + LANE_H / 2 + 4}
                  fontSize={12} textAnchor="end" fill="#475569"
                  className="font-mono"
                >
                  {lane.label.length > 18 ? '…' + lane.label.slice(-17) : lane.label}
                </text>
                {lane.subLabel && (
                  <text
                    x={PAD.left - 10} y={y + LANE_H / 2 + 18}
                    fontSize={10} textAnchor="end" fill="#94A3B8"
                  >
                    {lane.subLabel}
                  </text>
                )}
              </g>
            );
          })}

          {/* Hour gridlines */}
          <HourGridlines
            tMin={tMin} tMax={tMax} xOf={xOf}
            top={PAD.top} bottom={PAD.top + innerH}
            labelY={PAD.top + innerH + 18}
          />

          {/* "/proceed" annotation when in view */}
          {approvedAt && approvedAt >= tMin && approvedAt <= tMax && (
            <g>
              <line
                x1={xOf(approvedAt)} x2={xOf(approvedAt)}
                y1={PAD.top - 4} y2={PAD.top + innerH + 4}
                stroke="#22A06B" strokeWidth={1} strokeDasharray="2 2"
              />
              <text x={xOf(approvedAt) + 4} y={PAD.top + 10} fontSize={10} fill="#22A06B">/proceed</text>
            </g>
          )}

          {/* Session blocks */}
          {filteredLanes.map((lane, i) => {
            const y = laneY(i);
            return (
              <g key={`s-${lane.id}`}>
                {lane.sessions.map((s) => {
                  const start = new Date(s.startedAt).getTime();
                  const end = sessionEnd(s);
                  const x = xOf(start);
                  const w = Math.max(MIN_BLOCK_W, xOf(end) - x);
                  const c = colorFor(s.agentType);
                  const failed = s.outcome === 'crashed' || s.outcome === 'force_complete' || s.outcome === 'error';
                  // Hover-highlight: when the user is hovering an agent
                  // chip or block, fade blocks from other agents.
                  const dimmed = hoverAgent !== null && hoverAgent !== s.agentType;
                  const baseOpacity = s.isRunning ? 0.9 : failed ? 0.65 : 1;
                  const opacity = dimmed ? baseOpacity * 0.3 : baseOpacity;
                  // In story-mode, click routes to the session's own
                  // storyId. In agent-mode, lane.id is "agent:<name>" so
                  // we route to the session's storyId instead.
                  const targetStoryId = groupBy === 'agent' ? s.storyId : lane.id;
                  return (
                    <g
                      key={s.sessionId}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onOpenTranscript(targetStoryId, s.agentType)}
                      onMouseEnter={() => setHoverAgent(s.agentType)}
                      onMouseLeave={() => setHoverAgent(null)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onOpenTranscript(targetStoryId, s.agentType);
                        }
                      }}
                      tabIndex={0}
                    >
                      <title>
                        {agentEmoji(s.agentType)} {s.agentType} · {formatHm(s.startedAt)}
                        {s.finishedAt ? `–${formatHm(s.finishedAt)}` : ' (running)'}
                        {' · '}{formatMs(end - start)}
                        {' · '}{formatCost(s.costUsd)}
                        {s.outcome ? ` · ${s.outcome}` : ''}
                      </title>
                      <rect
                        x={x} y={y + 4} width={w} height={LANE_H - 8} rx={4}
                        fill={c.fill}
                        opacity={opacity}
                      >
                        {s.isRunning && !paused && (
                          <animate attributeName="opacity" values="1;0.55;1" dur="1.6s" repeatCount="indefinite" />
                        )}
                      </rect>
                      {failed && (
                        <rect
                          x={x} y={y + 4} width={w} height={LANE_H - 8} rx={4}
                          fill="none" stroke="#E5484D" strokeWidth={1.5}
                          opacity={dimmed ? 0.3 : 1}
                        />
                      )}
                      {w >= 56 && (
                        <text
                          x={x + 6} y={y + LANE_H / 2 + 4}
                          fontSize={11} fill={c.text}
                          opacity={dimmed ? 0.4 : 1}
                          style={{ pointerEvents: 'none', userSelect: 'none' }}
                        >
                          {agentEmoji(s.agentType)} {s.agentType}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })}

          {/* "Now" cursor */}
          <line
            x1={xOf(now)} x2={xOf(now)}
            y1={PAD.top - 4} y2={PAD.top + innerH + 4}
            stroke="#64748B" strokeWidth={1} strokeDasharray="3 3"
          />
          <text x={xOf(now)} y={PAD.top - 8} fontSize={10} fill="#64748B" textAnchor="middle">now</text>
        </svg>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function Segmented<T extends string>({
  label, value, onChange, options, testid,
}: {
  label: string;
  value: T;
  onChange: (next: T) => void;
  options: ReadonlyArray<{ value: T; label: string; disabled?: boolean }>;
  testid: string;
}) {
  return (
    <div className="inline-flex items-center gap-1.5 text-xs text-slate-600">
      <span>{label}</span>
      <div
        className="inline-flex bg-slate-100 rounded-md p-0.5"
        role="radiogroup"
        aria-label={label}
        data-testid={testid}
      >
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={o.disabled}
              onClick={() => !o.disabled && onChange(o.value)}
              data-testid={`${testid}-${o.value}`}
              className={
                'px-2.5 py-1 rounded text-xs font-medium transition-colors ' +
                (active
                  ? 'bg-white text-primary-700 shadow-sm'
                  : o.disabled
                  ? 'text-slate-400 cursor-not-allowed'
                  : 'text-slate-600 hover:text-slate-900')
              }
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function formatTick(t: number, interval: number): string {
  const d = new Date(t);
  if (interval >= 24 * 3_600_000) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

const HourGridlines: React.FC<{
  tMin: number; tMax: number;
  xOf: (t: number) => number;
  top: number; bottom: number; labelY: number;
}> = ({ tMin, tMax, xOf, top, bottom, labelY }) => {
  const span = tMax - tMin;
  const INTERVALS = [
    60_000, 5 * 60_000, 15 * 60_000, 30 * 60_000,
    60 * 60_000, 2 * 3_600_000, 6 * 3_600_000, 12 * 3_600_000, 24 * 3_600_000,
  ];
  const interval = INTERVALS.find((iv) => span / iv <= 8) ?? 24 * 3_600_000;

  // For day-scale intervals snap to local midnight; otherwise snap to epoch grid.
  let firstTick: number;
  if (interval >= 24 * 3_600_000) {
    const d = new Date(tMin);
    d.setHours(0, 0, 0, 0);
    if (d.getTime() < tMin) d.setDate(d.getDate() + 1);
    firstTick = d.getTime();
  } else {
    firstTick = Math.ceil(tMin / interval) * interval;
  }

  const ticks: number[] = [];
  for (let t = firstTick; t <= tMax; t += interval) ticks.push(t);
  return (
    <g>
      {ticks.map((t) => {
        const x = xOf(t);
        return (
          <g key={t}>
            <line x1={x} x2={x} y1={top} y2={bottom} stroke="#E2E8F0" strokeWidth={0.5} />
            <text x={x} y={labelY} fontSize={10} textAnchor="middle" fill="#94A3B8">
              {formatTick(t, interval)}
            </text>
          </g>
        );
      })}
    </g>
  );
};

export default EpicPipeline;
